<script setup>
  import { computed } from 'vue'
  import quizIllustration from '@/assets/images/bcst-quiz-illustration.svg'
  import BcstLessonBadge from './BcstLessonBadge.vue'
  import { useTransformLessonCard } from '../../composables/useTransformLessonCard'

  const props = defineProps({
    lesson: { type: Object, required: true },
    // 1-based sequential index of this lesson in the module's sorted lessons list.
    index: { type: Number, required: true },
    // Pre-resolved Firebase Storage URL (null if the lesson has no thumbnail or it is unresolved).
    thumbnailUrl: { type: String, default: null },
    // False only for video lessons that have no associated quiz in the module.
    hasAssociatedQuiz: { type: Boolean, default: true },
    // True when this lesson is the first incomplete one in the module.
    isNextInQueue: { type: Boolean, default: false },
    // { [lessonId]: 1-based index } — used to resolve the required lesson's display number.
    lessonIndexMap: { type: Object, default: () => ({}) },
  })

  const {
    isVideo,
    isQuiz,
    isWatched,
    isInProgress,
    isLocked,
    isPassed,
    isFailed,
    questionLabel,
    plainDescription,
    isRestarting,
    goToLesson,
    goToRequiredLesson,
  } = useTransformLessonCard(() => props.lesson)

  const cardTitle = computed(() => {
    const prefix = isQuiz.value ? 'Quiz' : 'Lesson'
    // Per-type presentation number from the store (videos and quizzes numbered
    // independently); falls back to the raw index only if it's unavailable.
    const number = props.lesson.displayNumber ?? props.index
    return `${prefix} ${number}: ${props.lesson.title}`
  })

  const videoMeta = computed(() => {
    if (!isVideo.value) return ''
    const dur = props.lesson.durationLabel
    const label = isWatched.value ? 'Watched' : isInProgress.value ? 'In Progress' : 'Not Started'
    return dur ? `${dur}  •  ${label}` : label
  })

  const quizMeta = computed(() => {
    if (!isQuiz.value) return ''
    const score = props.lesson.quiz?.scorePercent
    return score !== null && score !== undefined
      ? `${questionLabel.value}  •  Score ${Math.round(score)}%`
      : questionLabel.value
  })

  const ctaLabel = computed(() => {
    if (isVideo.value) {
      if (isWatched.value) return 'Rewatch Video'
      if (isInProgress.value) return 'Continue Video Lesson'
      return 'Go to Video Lesson'
    }
    if (isPassed.value || isFailed.value) return 'Retake Quiz'
    if (isInProgress.value) return 'Continue Quiz'
    return 'Take Quiz'
  })

  // 1-based display index of the required (prerequisite) video lesson.
  const requiredLessonDisplayIndex = computed(() => {
    if (!isLocked.value) return null
    const reqId = props.lesson.quiz?.requiredLessonId
    return reqId ? (props.lessonIndexMap[reqId] ?? null) : null
  })
</script>

<template>
  <div class="card tlc-card h-100">

    <!-- ── Media area: real thumbnail when available, otherwise type-specific placeholder ── -->
    <div class="tlc-media">
      <!-- Real thumbnail from Storage (video or quiz) -->
      <template v-if="thumbnailUrl">
        <img
          :src="thumbnailUrl"
          :alt="lesson.title"
          loading="lazy"
          decoding="async"
          class="tlc-media-img" />
      </template>

      <!-- Video: play-circle placeholder when no thumbnail exists -->
      <div v-else-if="isVideo" class="tlc-media-placeholder d-flex align-items-center justify-content-center">
        <i class="bi bi-play-circle tlc-placeholder-icon"></i>
      </div>

      <!-- Quiz: undraw illustration when no thumbnail exists -->
      <div v-else-if="isQuiz" class="tlc-media-illustration d-flex align-items-center justify-content-center">
        <img :src="quizIllustration" alt="" class="tlc-illustration-img" />
      </div>

    </div>

    <!-- ── Card body ── -->
    <div class="card-body d-flex flex-column tlc-body">

      <!-- Badge: above the title, margin-bottom 13.6px (Figma position) -->
      <!-- "Next in Queue" only shows for a not-started next lesson — hidden when it's
           already in progress (that lesson is the active one, not an upcoming queue item). -->
      <!-- Progress-state badge (mutually exclusive) + an ADDITIVE "Locked" badge.
           Locked is the quiz's availability, not its progress: it must sit alongside
           the In Progress / Not Started / … state badge, never replace it. -->
      <div class="tlc-badge-area">
        <BcstLessonBadge v-if="isNextInQueue && !isInProgress" variant="next-in-queue">Next in Queue</BcstLessonBadge>
        <BcstLessonBadge v-if="isLocked" variant="locked">Locked</BcstLessonBadge>
        <BcstLessonBadge v-if="isWatched" variant="success">Complete</BcstLessonBadge>
        <BcstLessonBadge v-else-if="isPassed" variant="success">Passed</BcstLessonBadge>
        <BcstLessonBadge v-else-if="isFailed" variant="danger">Not Passed</BcstLessonBadge>
        <BcstLessonBadge v-else-if="isInProgress" variant="in-progress">In Progress</BcstLessonBadge>
        <BcstLessonBadge v-else variant="not-started">Not Started</BcstLessonBadge>
      </div>

      <!-- Title: "Lesson N: …" or "Quiz N: …" -->
      <p class="tlc-title">{{ cardTitle }}</p>

      <!-- Meta: "{dur} • Watched/Not Started" for video; "{N} Questions [• Score X%]" for quiz -->
      <p v-if="videoMeta || quizMeta" class="tlc-meta">
        {{ isVideo ? videoMeta : quizMeta }}
      </p>

      <!-- Description (video or quiz, plain text from bodyHtml/descriptionHtml, 3-line clamp) -->
      <p v-if="plainDescription" class="tlc-desc text-14 text-muted flex-grow-1">
        {{ plainDescription }}
      </p>
      <div v-else class="flex-grow-1"></div>

      <!-- "No associated quiz" note (video lessons only when no quiz depends on them) -->
      <p v-if="isVideo && !hasAssociatedQuiz" class="tlc-no-quiz-note text-14 mb-3">
        Note: This lesson does not have an associated quiz.
      </p>

      <!-- Footer: CTA button OR locked text -->
      <div class="tlc-footer mt-auto">
        <!-- Locked quiz: show instructional text instead of button -->
        <template v-if="isLocked">
          <p class="tlc-locked-text text-14 fw-bold mb-0">
            This quiz will be available once you've completed the associated lesson.
            <span
              class="tlc-locked-link text-primary"
              role="button"
              tabindex="0"
              @click="goToRequiredLesson"
              @keydown.enter.prevent="goToRequiredLesson"
              @keydown.space.prevent="goToRequiredLesson">
              Go to Lesson {{ requiredLessonDisplayIndex }}.
            </span>
          </p>
        </template>

        <!-- Normal CTA (video always; unlocked quiz) -->
        <button
          v-else
          class="btn btn-primary tlc-cta w-100"
          :disabled="isRestarting"
          @click="goToLesson">
          <template v-if="isRestarting">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ ctaLabel }}
          </template>
          <template v-else>
            {{ ctaLabel }}
            <i class="bi bi-chevron-right ms-2"></i>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* Card container — mirrors Figma rectangle: 1px border gray-400, radius 4px */
  .tlc-card {
    border: 1px solid var(--bs-gray-400);
    border-radius: 4px;
    overflow: hidden;
  }

  /* ── Media area ─────────────────────────────────────────────── */
  .tlc-media {
    width: 100%;
    height: 200px;   /* ~16:9 at 380px card width */
    overflow: hidden;
    background-color: var(--bs-gray-200);
    flex-shrink: 0;
  }

  .tlc-media-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .tlc-media-placeholder {
    width: 100%;
    height: 100%;
  }

  .tlc-placeholder-icon {
    font-size: 3rem;
    color: var(--bs-gray-500);
  }

  /* Quiz illustration: centered, fixed size, light bg */
  .tlc-media-illustration {
    width: 100%;
    height: 100%;
    background-color: var(--bs-gray-200);
  }

  .tlc-illustration-img {
    max-width: 85%;
    max-height: 85%;
    object-fit: contain;
  }

  /* Badge area: in card body, above the title — Figma margin-bottom 13.6px.
     Individual badge styling lives in the shared BcstLessonBadge component. */
  .tlc-badge-area {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-bottom: 13.6px;
  }

  /* ── Card body ─────────────────────────────────────────────── */
  .tlc-body {
    padding: 15px 22px;
  }

  /* Title: Text - 16 - Bold, Eggplant primary */
  .tlc-title {
    color: var(--bs-primary);
    font-family: 'DM Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
    margin-bottom: 6px;
  }

  /* Meta: Text - 14 - Medium, body color */
  .tlc-meta {
    color: var(--bs-body-color);
    font-family: 'DM Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 18.2px */
    letter-spacing: -0.28px;
    margin-bottom: 8px;
  }

  .tlc-desc {
    line-height: 1.3;
    letter-spacing: -0.28px;
    margin-bottom: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .tlc-no-quiz-note {
    color: var(--bs-gray-600);
    font-style: italic;
    line-height: 1.3;
    letter-spacing: -0.28px;
  }

  /* ── Footer ─────────────────────────────────────────────────── */
  .tlc-footer {
    padding-top: 15px;
  }

  .tlc-cta {
    color: var(--Gray-White, #FFF);
    font-family: "DM Sans";
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 20.8px */
    padding: 8px 12px;
    border-radius: 4px;
  }

  /* Locked text: 14px bold body color + purple link (no underline by default) */
  .tlc-locked-text {
    color: var(--bs-body-color);
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .tlc-locked-link {
    cursor: pointer;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
    }
  }
</style>
